import { computed, runInInjectionContext, signal, Signal, untracked } from '@angular/core';
import { WbActionCommand, WbActionResult } from '@workbuddy/ui/v2/components/action.bar';
import { metadataActionCommandId, type ActionCommandDescriptor } from '../contracts/surface.capabilities';
import type { SurfaceContextHandle } from '../runtime/surface.context/create.surface.context.options';

/** Mount-stable executable action and its reactive presentation descriptor. */
export interface ResolvedSurfaceAction {
  /** Reactive label, icon, visibility, placement, and disabled state. */
  readonly descriptor: Signal<ActionCommandDescriptor>;
  /** Executes the capability against the page from which it was resolved. */
  readonly run: () => WbActionResult | Promise<WbActionResult>;
}

type SurfaceActionFallback = (reference: string) => ResolvedSurfaceAction | undefined;

/**
 * Resolves each command key and creates its descriptor signal. Call this when the
 * handle changes, not on every model change, so capability factories run once per mount.
 */
export function resolveSurfaceActions(handle: SurfaceContextHandle, fallback?: SurfaceActionFallback): readonly ResolvedSurfaceAction[] {

  const commands = handle.compiled.actionsSlot?.commands ?? [];
  return untracked(() => commands
    .map(reference => {
      const metadataCommandId = metadataActionCommandId(reference);
      const capability = metadataCommandId ? undefined : handle.resolver?.resolve('actionCommands', reference);
      const resolved = capability
        ? { descriptor: capability.descriptor({ page: handle.page }), run: () => capability.run(handle.page) }
        : fallback?.(metadataCommandId ?? reference);
      return resolved ? guardInFlight({ ...resolved, run: bindActionRun(handle, resolved.run) }) : undefined;
    })
    .filter((action): action is ResolvedSurfaceAction => !!action));
}

/**
 * The action bar fires `run` without awaiting it, so a double click or a repeated shortcut would
 * send the request twice (two approvals, two emails). While a run is in flight the command reports
 * disabled and further runs are dropped.
 */
function guardInFlight(action: ResolvedSurfaceAction): ResolvedSurfaceAction {

  const $running = signal(false);
  const descriptor = computed<ActionCommandDescriptor>(() => ($running() ? { ...action.descriptor(), disabled: true } : action.descriptor()));
  const run = async (): Promise<WbActionResult> => {

    if (untracked($running)) return;
    $running.set(true);
    try {
      return await action.run();
    } finally {
      $running.set(false);
    }
  };
  return { descriptor, run };
}

/** Action-bar clicks and shortcuts are outside Angular injection context; `promptReason` / `inject()` need it. */
function bindActionRun(handle: SurfaceContextHandle, run: ResolvedSurfaceAction['run']): ResolvedSurfaceAction['run'] {

  return () => runInInjectionContext(handle.injector, () => run());
}

/**
 * Reads current descriptors, removes hidden commands, and keeps every supported
 * action-bar property. `forceDisabled` is useful while a host is saving or initializing.
 */
export function toSurfaceActionCommands(actions: readonly ResolvedSurfaceAction[], forceDisabled = false): readonly WbActionCommand[] {

  const resolved = actions
    .map(action => ({ descriptor: action.descriptor(), run: action.run }))
    .filter(action => !action.descriptor.hidden);
  const byId = new Map(resolved.map(action => [action.descriptor.id, action]));
  const nestedIds = new Set(resolved.flatMap(action => action.descriptor.items ?? []));
  const toCommand = ({ descriptor, run }: typeof resolved[number]): WbActionCommand => ({
    id: descriptor.id,
    label: descriptor.label,
    iconLeft: descriptor.iconLeft,
    disabled: descriptor.disabled || forceDisabled,
    tooltip: descriptor.tooltip,
    pin: descriptor.pin,
    kind: descriptor.kind,
    displayStyle: descriptor.displayStyle,
    accent: descriptor.accent,
    run,
    items: descriptor.items?.map(id => byId.get(id)).filter(action => !!action).map(toCommand),
  });

  return resolved.filter(action => !nestedIds.has(action.descriptor.id)).map(toCommand);
}
